import {
    ThemeVariant,
    CLIENT_THEME_VARIANT,
} from 'hooks/useThemeStyling/useThemeStyling'
import { updateRecordDates } from './updateMockDataDates'
// import { addDays, format, isBefore } from 'date-fns'

/**
 * Find the variant name from the theme variant ID
 * @param themeVariant The theme variant object
 * @returns The variant name (e.g., 'AMADEUS', 'KRAKEN')
 */
const findVariantName = (themeVariant: ThemeVariant): string => {
    const entry = Object.entries(CLIENT_THEME_VARIANT).find(
        ([_, variant]) => variant.id === themeVariant.id
    )

    if (!entry) {
        throw new Error(`Unknown theme variant ID: ${themeVariant.id}`)
    }

    return entry[0] // Return the key (variant name)
}

// !Hardcoded a date ('2026-01-05') to compare to in order to determine number of days to offset mock payload:
const FIXED_REFERENCE_DATE = '2026-01-05'

/**
 * Returns a signed number of days offset:
 * Positive if fixed date is in the past
 * Negative if fixed date is in the future
 * @param fixedDateStr - Fixed date in "YYYY-MM-DD" format
 * @returns number of days offset
 */
const getDaysOffset = (fixedDateStr: string): number => {
    // Parse fixed date in local time
    const [year, month, day] = fixedDateStr.split('-').map(Number)
    const fixedDate = new Date(year, month - 1, day) // month is 0-indexed

    // Normalize today's date to local midnight
    const today = new Date()
    const todayLocal = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
    )

    // Calculate difference in days
    const diffMs = fixedDate.getTime() - todayLocal.getTime()
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))

    return -diffDays // flip sign: past = positive, future = negative
}

/**
 * Get mock data for a specific client and grid type
 * @param currentThemeVariant The current theme variant object
 * @param gridType The type of grid data to load (e.g., 'Closed', 'InProgress')
 * @returns The mock data for the specified client and grid type
 */
export const getClientMockData = (
    currentThemeVariant: ThemeVariant,
    gridType: string,
    tabType?: string
) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            const mockData = require(`views/CaseManagementAGGrid/grids/mocks/${folderName}/${gridType}.json`)

            // const today = new Date()
            // const currentMonth = today.getMonth() - 1
            // dataset.data.forEach((item: any) => {
            //     // Check if creation_date exists
            //     if (item.creation_date) {
            //         let [year, , day] = item.creation_date.split('-').map(Number)

            //         // Create new date with current month (preserve year and day)
            //         const updatedCreationDate = new Date(year, currentMonth, day)

            //         // Format as 'YYYY-MM-DD'
            //         item.creation_date = updatedCreationDate
            //             .toISOString()
            //             .split('T')[0]

            //         // If due_date exists, update it to be 30 days after new creation_date
            //         if (item.due_date) {
            //             const today = new Date()
            //             const creationDate = new Date(updatedCreationDate)
            //             const dueDate30 = addDays(creationDate, 30)

            //             // if due_date (30 days out) has passed then extend to 45 days
            //             const daysToAdd = isBefore(dueDate30, today) ? 45 : 30
            //             const dueDate = addDays(creationDate, daysToAdd)
            //             item.due_date = format(dueDate, 'yyyy-MM-dd')
            //         }
            //     }
            // })

            const offset: number = getDaysOffset(FIXED_REFERENCE_DATE)
            const updatedMockData = updateRecordDates(mockData, offset)

            if (tabType) {
                return {
                    data: updatedMockData.data.filter((i: any) =>
                        i.tiles?.includes(tabType)
                    ),
                }
            }
            return { data: updatedMockData.data }
        } catch (error) {
            throw new Error(
                `Failed to load mock data for ${gridType} in ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(`Error loading mock data:`, error)
        throw error
    }
}

/**
 * Get mock data for chargebacks
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for chargebacks
 */
export const getReconciliationMockData = (currentThemeVariant: ThemeVariant) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            const mockData = require(`views/Reconciliation/grids/mocks/${folderName}/MasterDetail.json`)

            return { data: mockData.data }
        } catch (error) {
            throw new Error(
                `Failed to load Reconciliation mock data for ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(`Error loading Reconciliation mock data:`, error)
        throw error
    }
}

/**
 * Get mock data for chargebacks
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for chargebacks
 */
export const getReconciliationLogsMockData = (currentThemeVariant: ThemeVariant) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            const mockData = require(`views/ReconciliationLogs/grids/mocks/${folderName}/MasterDetail.json`)
            
            const offset: number = getDaysOffset(FIXED_REFERENCE_DATE)
            const updatedMockData = updateRecordDates(mockData, offset)
            return { data: updatedMockData.data }
        } catch (error) {
            throw new Error(
                `Failed to load Reconciliation Logs mock data for ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(`Error loading Reconciliation Logs mock data:`, error)
        throw error
    }
}
/**
 * Get mock data for chargebacks
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for chargebacks
 */
export const getRetrievalsMockData = (currentThemeVariant: ThemeVariant) => {
    try {
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        try {
            const mockData = require(`views/Reconciliation/grids/mocks/${folderName}/RetrievalsMasterDetail.json`)

            const offset: number = getDaysOffset(FIXED_REFERENCE_DATE)
            const updatedMockData = updateRecordDates(mockData, offset)
            return { data: updatedMockData.data }
        } catch (error) {
            throw new Error(
                `Failed to load Retrievals mock data for ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(`Error loading Retrievals mock data:`, error)
        throw error
    }
}

export const getReconLogsFilesMockData = () => {
    try {
        const mockData = require('views/ReconciliationLogs/grids/mocks/FilesMockData.json')
        return { data: mockData.data }
    } catch (error) {
        console.error('Error loading Recon Logs Files mock data:', error)
        throw error
    }
}

export const getReconLogsApprovalsMockData = () => {
    try {
        const mockData = require('views/ReconciliationLogs/grids/mocks/ApprovalsMockData.json')
        return { data: mockData.data }
    } catch (error) {
        console.error('Error loading Recon Logs Approvals mock data:', error)
        throw error
    }
}

export const getChargebacksMockData = (currentThemeVariant: ThemeVariant) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            const mockData = require(`views/ChargebacksAGGrid/grids/mocks/${folderName}/MasterDetail.json`)
            
            const offset: number = getDaysOffset(FIXED_REFERENCE_DATE)
            const updatedMockData = updateRecordDates(mockData, offset)
            return { data: updatedMockData.data }
        } catch (error) {
            throw new Error(
                `Failed to load chargebacks mock data for ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(`Error loading chargebacks mock data:`, error)
        throw error
    }
}

/**
 * Get mock data for invalid chargebacks
 * @returns The mock data for invalid chargebacks
 */
export const getInvalidChargebacksMockData = () => {
    try {
        const mockData = require(`views/InvalidChargebacksAGGrid/grids/mocks/InvalidChargebacks.json`)

        const offset: number = getDaysOffset(FIXED_REFERENCE_DATE)
        const updatedMockData = updateRecordDates(mockData, offset)
        return { data: updatedMockData.data }
    } catch (error) {
        console.error('Error loading invalid chargebacks mock data:', error)
        throw new Error(
            `Failed to load invalid chargebacks mock data: ${error}`
        )
    }
}

/**
 * Get mock data for webhooks
 * @returns The mock data for webhooks
 */
export const getWebhooksMockData = (gridType: string) => {
    try {
        if (gridType === 'caseStatus') {
            return require(`views/Webhooks/grids/mocks/WebhooksCaseStatus.json`)
        }
        if (gridType === 'verdictStatus') {
            return require(`views/Webhooks/grids/mocks/WebhooksVerdictStatus.json`)
        }
    } catch (error) {
        console.error('Error loading webhooks mock data:', error)
        throw new Error(`Failed to load webhooks mock data: ${error}`)
    }
}

/**
 * Get mock data for dashboard's top 5 accounts
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for dashboard's top 5 accounts
 */
export const getTop5AccountsMockData = (currentThemeVariant: ThemeVariant) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            return require(`views/Dashboard/grids/mocks/${folderName}/top5AccountNumbers.json`)
        } catch (error) {
            throw new Error(
                `Failed to load dashboard's top 5 accounts mock data for ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(
            `Error loading dashboard's top 5 accounts mock data:`,
            error
        )
        throw error
    }
}

/**
 * Get mock data for Brand Reports
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for Brand Reports
 */
export const getBrandReportsMockData = (currentThemeVariant: ThemeVariant) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            return require(`views/BrandReports/mocks/${folderName}/BrandReports.json`)
        } catch (error) {
            throw new Error(
                `Failed to load Brand Reports mock data for ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(`Error loading Brand Reports mock data:`, error)
        throw error
    }
}
/**
 * Get mock data for Brand Reports
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for Brand Reports
 */
export const getAccountReportsMockData = (
    currentThemeVariant: ThemeVariant,
    type: 'merchant' | 'account'
) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            if (type === 'merchant') {
                return require(`views/Reports/AccountReports/mocks/${folderName}/MerchantReports.json`)
            }
            return require(`views/Reports/AccountReports/mocks/${folderName}/AccountReports.json`)
        } catch (error) {
            throw new Error(
                `Failed to load Brand Reports mock data for ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(`Error loading Brand Reports mock data:`, error)
        throw error
    }
}

/**
 * Get mock data for Account Settings tree
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for Account Settings tree
 */
export const getAccountsMockData = (currentThemeVariant: ThemeVariant) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            return require(`views/AccountSettings/trees/mocks/${folderName}/Accounts.json`)
        } catch (error) {
            throw new Error(
                `Failed to load accounts mock data for ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(`Error loading accounts mock data:`, error)
        throw error
    }
}

/**
 * Get mock data for Account Settings's Account Numbers
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for Account Settings's Account Numbers
 */
export const getAccountNumbersMockData = (
    merchantId: number,
    currentThemeVariant: ThemeVariant
) => {
    const defaultMock = {
        data: [],
        pagination: {
            start: 0,
            limit: 50,
            total: 0,
        },
    }
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            const mockData = require(`views/AccountSettings/trees/mocks/${folderName}/AccountNumbers.json`)
            return mockData[merchantId] || defaultMock
        } catch (error) {
            return defaultMock
        }
    } catch (error) {
        console.error(`Error loading account numbers mock data:`, error)
        throw error
    }
}

/**
 * Get mock data for Dashboard's Tile values
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for Dashboard's Tile values
 */
export const getDashboardTilesMockData = (
    data: { [key: string]: any },
    currentThemeVariant: ThemeVariant
) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            const mockData = require(`views/Dashboard/mocks/${folderName}/TilesData.json`)
            return {
                ...mockData,
                chargebacks: {
                    metric: data.chargebacks?.metric,
                },
            }
        } catch (error) {
            return data
        }
    } catch (error) {
        console.error(`Error loading dashboard's tile values mock data:`, error)
        throw error
    }
}

/**
 * Get mock data for on-demand case builder
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for on-demand case builder
 */
export const getOnDemandCaseBuilderMockData = (
    step: number,
    currentThemeVariant: ThemeVariant,
    properties: { isFraud: boolean; showAllDisputeReason: boolean }
) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            const mockData = require(`views/OnDemandCaseBuilderIssuer/mocks/CaseBuilder.json`)
            if (step === 2) {
                return properties.showAllDisputeReason
                    ? mockData[`${step}AllDisputeReason`] || {}
                    : mockData[`${step}SelectDisputeReason`] || {}
            }
            if (step === 3) {
                return properties.isFraud
                    ? mockData[`${step}Fraud`] || {}
                    : mockData[`${step}NonFraud`] || {}
            }
            return mockData[step] || {}
        } catch (error) {
            throw new Error(
                `Failed to load on-demand case builder mock data for ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(`Error loading on-demand case builder mock data:`, error)
        throw error
    }
}

/**
 * Get mock data for Cardholder Feedback
 * @param currentThemeVariant The current theme variant object
 * @returns The mock data for Cardholder Feedback
 */
export const getCardholderFeedbackMockData = (
    currentThemeVariant: ThemeVariant
) => {
    try {
        // Get the folder name directly from the theme variant object
        const folderName = currentThemeVariant.mockDataFolder

        if (!folderName) {
            throw new Error(
                `No mockDataFolder defined for theme variant: ${findVariantName(
                    currentThemeVariant
                )}`
            )
        }

        // Load the mock data using require (synchronous)
        try {
            return require(`views/OnDemandCardholderFeedback/mocks/${folderName}/CardholderFeedback.json`)
        } catch (error) {
            throw new Error(
                `Failed to load cardholder feedback mock data for ${folderName} folder: ${error}`
            )
        }
    } catch (error) {
        console.error(`Error loading cardholder feedback mock data:`, error)
        throw error
    }
}

/**
 * Get updated data for Account Numbers grid on Account Settings
 * @returns The mock data for Account Numbers
 */
export const updateAccountNumbersData = (data: any) => {
    const generateRandomPhone = () => {
        const rand3 = () => Math.floor(100 + Math.random() * 900) // 3-digit segment
        const rand4 = () => Math.floor(1000 + Math.random() * 9000) // 4-digit segment
        return `1-${rand3()}-${rand3()}-${rand4()}`
    }

    const firstNames = [
        'Alex',
        'Taylor',
        'Jordan',
        'Casey',
        'Riley',
        'Morgan',
        'Jamie',
        'Avery',
        'Logan',
        'Skyler',
        'Reese',
        'Drew',
        'Emerson',
        'Cameron',
        'Quinn',
        'Peyton',
        'Dakota',
        'Harper',
        'Rowan',
        'Sage',
        'Elliot',
        'Finley',
        'Remy',
        'Blake',
        'Kai',
    ]

    const lastNames = [
        'Smith',
        'Johnson',
        'Brown',
        'Davis',
        'Miller',
        'Wilson',
        'Anderson',
        'Thomas',
        'Jackson',
        'White',
        'Harris',
        'Martin',
        'Thompson',
        'Garcia',
        'Martinez',
        'Robinson',
        'Clark',
        'Rodriguez',
        'Lewis',
        'Lee',
        'Walker',
        'Hall',
        'Allen',
        'Young',
        'King',
    ]

    const getRandomItem = (arr: string[]) => {
        return arr[Math.floor(Math.random() * arr.length)]
    }
    return {
        ...data,
        data: data.data.map((item: any) => {
            const firstName = getRandomItem(firstNames)
            const lastName = getRandomItem(lastNames)
            return {
                ...item,
                scheme: Math.random() < 0.5 ? 'Visa' : 'Mastercard',
                internal_reference_id: Math.floor(
                    100000000 + Math.random() * 900000000
                ),
                group_id: Math.floor(1000000 + Math.random() * 9000000),
                first_name: firstName,
                last_name: lastName,
                email: `${firstName}@${item.merchant_name?.replace(
                    ' ',
                    ''
                )}.com`.toLowerCase(),
                phone: generateRandomPhone(),
                platform_name: `${item.merchant_name} - ${item.alias}`,
                platform_url_location: `https://${item.merchant_name
                    ?.replace(' ', '')
                    .toLowerCase()}-${item.alias}.com`,
                platform_username: `${item.alias}`,
                platform_password: '••••••••',
            }
        }),
    }
}
